import {
  faBalanceScale,
  faBed,
  faBriefcase,
  faBus,
  faCoffee,
  faConciergeBell,
  faEgg,
  faEllipsisH,
  faGavel,
  faGlassCheers,
  faGraduationCap,
  faHamburger,
  faLaptop,
  faNewspaper,
  faParking,
  faPlane,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';

/**
 * This map is the single source of truth for category -> icon matching.
 * Keep the keys aligned with the category values used by the backend and locales.
 * When a category has no specific icon, we intentionally fall back to the "other" icon.
 *
 * XPEN-3191 / XPEN-3183-008 (D7): rewritten for the sixteen XPENS categories,
 * English and French, replacing the retired 27-key English-only map. This is
 * the first release in which French-speaking clients see icons at all — the
 * old map's keys were all English, so a French category name always fell
 * back to `other`.
 *
 * Note: "diner" (French Lunch) vs "dinner" (English Dinner): French "Dîner"
 * means lunch; English "Dinner" is the evening meal. Normalised they are one
 * letter apart with opposite meanings. Do not "fix" this — it is correct.
 *
 * XPEN-3435: Parking added after the sixteen.
 */
const expenseCategoryIcons = {
  other: faEllipsisH,
  autre: faEllipsisH,

  breakfast: faEgg,
  petitdejeuner: faEgg,

  lunch: faHamburger,
  diner: faHamburger,

  dinner: faUtensils,
  souper: faUtensils,

  coffeesnacks: faCoffee,
  cafeetcollations: faCoffee,

  mealsunspecified: faConciergeBell,
  repasnonprecise: faConciergeBell,

  entertainmentclienthospitality: faGlassCheers,
  divertissementetaccueildeclients: faGlassCheers,

  airtravel: faPlane,
  transportaerien: faPlane,

  groundtransportation: faBus,
  transportterrestre: faBus,

  parking: faParking,
  stationnement: faParking,

  lodging: faBed,
  hebergement: faBed,

  clientdisbursementscourtfees: faGavel,
  deboursesclientsetfraisjudiciaires: faGavel,

  legalresearchprofessionaldues: faBalanceScale,
  recherchejuridiqueetcotisationsprofessionnelles: faBalanceScale,

  technologysoftwaretelecom: faLaptop,
  technologielogicielsettelecommunications: faLaptop,

  trainingcleconferences: faGraduationCap,
  formationformationcontinueetconferences: faGraduationCap,

  officesuppliesequipment: faBriefcase,
  fournituresetequipementdebureau: faBriefcase,

  duesandsubscriptions: faNewspaper,
  cotisationsabonnements: faNewspaper,
};

/**
 * Normalizes category values before lookup so the UI behaves safely with
 * inconsistent casing, whitespace, punctuation, or accents from callers.
 *
 * D7c: strips accents (via Unicode NFD decomposition + combining-mark
 * removal) before lower-casing and stripping every non-alphanumeric
 * character. Replaces a whitespace-only strip that let three traps through:
 * the en dash in "Meals – Unspecified", the commas in two category names,
 * and every accented character in the French names.
 *
 * @param {string | null | undefined} category - Raw category value from the UI or API.
 * @returns {string}
 */
function normalizeExpenseCategory(category) {
  if (typeof category !== 'string') {
    return '';
  }

  return category
    .normalize('NFD')
    // Strip combining diacritical marks (U+0300–U+036F) left behind by NFD
    // decomposition. Written as an escaped Unicode range, not literal
    // characters, so this line stays readable in a diff.
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Returns the Font Awesome icon for a known expense category.
 * If the category is unknown or empty, the function returns the shared "other" icon.
 *
 * @param {string | null | undefined} category - Expense category key.
 * @returns {import('@fortawesome/fontawesome-svg-core').IconDefinition}
 */
export function getExpenseCategoryIcon(category) {
  const normalizedCategory = normalizeExpenseCategory(category);
  return expenseCategoryIcons[normalizedCategory] || expenseCategoryIcons.other;
}

export default getExpenseCategoryIcon;
